import { useState, type ReactNode } from 'react';
import {
  ChevronRight,
  FileText,
  Filter,
  MessageSquare,
  Send,
  Thermometer,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  ABORDAGEM_ACOES,
  ABORDAGEM_CANAIS,
  ABORDAGEM_RESULTADOS,
  RECUSA_MOTIVOS,
  TEMPERATURA_LABEL,
  type Estagio,
  type Evento,
  type Temperatura,
} from '../schemas';
import { DossieMarkdown } from './DossieMarkdown';

interface Props {
  estagio: Estagio;
  eventos: Evento[];
  oportunidade: Record<string, unknown>;
  dossieTexto?: string | null | undefined;
}

interface CollapsibleProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string | null;
  trailing?: ReactNode;
  children: ReactNode;
}

// Card colapsável padrão "ver mais" — mesma estrutura visual do ImovelCard
// do diagnóstico (icon-box accent + título + subtítulo + chevron rotativo).
function Collapsible({ icon: Icon, title, subtitle, trailing, children }: CollapsibleProps) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);

  return (
    <div className="border border-border rounded-xl bg-elevated">
      <div
        className="flex items-center gap-3 px-3 py-3 cursor-pointer"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span
          className="shrink-0 inline-flex items-center justify-center rounded-lg"
          style={{
            width: 36,
            height: 36,
            background: 'rgba(217,119,87,0.10)',
            color: '#d97757',
          }}
        >
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-bold text-text leading-tight truncate">
            {title}
          </span>
          {subtitle && (
            <span className="text-xs text-text-muted truncate">{subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {trailing}
          <span className="text-xs text-text-muted hidden sm:inline">
            {open ? 'Mostrar menos' : 'Ver mais'}
          </span>
          <ChevronRight
            size={16}
            strokeWidth={1.5}
            className="text-text-muted transition-transform"
            style={{ transform: open ? 'rotate(90deg)' : undefined }}
          />
        </div>
      </div>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border">{children}</div>
      )}
    </div>
  );
}

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

function formatBRL(n: number | string | null | undefined) {
  if (n == null || n === '') return null;
  const num = typeof n === 'number' ? n : Number(n);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function Linha({ label, valor }: { label: string; valor: ReactNode }) {
  if (valor == null || valor === '') return null;
  return (
    <div className="flex items-baseline gap-2 py-1">
      <span className="text-[0.7rem] uppercase tracking-wide text-text-muted shrink-0 w-32">
        {label}
      </span>
      <span className="text-sm text-text">{valor}</span>
    </div>
  );
}

// ─── Card de Triagem (resultado: status_sinal) ──────────────────────────────
function CardTriagem({ eventos }: { eventos: Evento[] }) {
  const ev = eventos.find((e) => e.tipo === 'triada');
  if (!ev) return null;
  const p = (ev.payload as Record<string, unknown> | null) ?? {};
  const status = String(p.status_sinal ?? '');
  return (
    <Collapsible
      icon={Filter}
      title="Triagem do analista"
      subtitle={`${status === 'confirmado' ? 'Confirmado' : status === 'falso_positivo' ? 'Falso positivo — virou laudo' : status === 'duplicado' ? 'Duplicado' : 'Ruído'} · ${formatDate(ev.created_at)}`}
    >
      <div className="flex flex-col gap-1 py-2">
        <Linha label="Status do sinal" valor={status} />
        {Boolean(p.motivo) && <Linha label="Motivo" valor={String(p.motivo)} />}
        {ev.ator_nome && <Linha label="Por" valor={ev.ator_nome} />}
      </div>
    </Collapsible>
  );
}

// ─── Card de Qualificação ──────────────────────────────────────────────────
const MOTIVO_LABEL: Record<string, string> = {
  urgencia_regulatoria: 'Urgência regulatória',
  risco_credito: 'Bloqueio de crédito rural',
  ticket_alto: 'Ticket alto',
  cliente_engajado: 'Cliente engajado',
  janela_safra: 'Janela de safra',
  dependencia_outro_servico: 'Depende de outro serviço',
  baixa_prioridade: 'Baixa prioridade',
  outro: 'Outro',
};

function CardQualificacao({ eventos, oportunidade }: { eventos: Evento[]; oportunidade: Record<string, unknown> }) {
  const ev = eventos.find((e) => e.tipo === 'qualificada');
  if (!ev) return null;
  const p = (ev.payload as Record<string, unknown> | null) ?? {};
  const temp = (p.temperatura ?? oportunidade.temperatura) as Temperatura | undefined;
  const potencialMin = formatBRL(p.potencial_min as number | null ?? oportunidade.potencial_min as number | null);
  const potencialMax = formatBRL(p.potencial_max as number | null ?? oportunidade.potencial_max as number | null);
  const motivo = p.motivo_qualificacao ? MOTIVO_LABEL[String(p.motivo_qualificacao)] ?? String(p.motivo_qualificacao) : null;

  return (
    <Collapsible
      icon={Thermometer}
      title="Qualificação"
      subtitle={`${temp ? TEMPERATURA_LABEL[temp] : '—'}${motivo ? ' · ' + motivo : ''} · ${formatDate(ev.created_at)}`}
    >
      <div className="flex flex-col gap-1 py-2">
        {temp && <Linha label="Temperatura" valor={TEMPERATURA_LABEL[temp]} />}
        {motivo && <Linha label="Motivo" valor={motivo} />}
        {(potencialMin || potencialMax) && (
          <Linha label="Potencial" valor={`${potencialMin ?? '?'} – ${potencialMax ?? '?'}`} />
        )}
        {Boolean(p.prazo_abordagem) && (
          <Linha label="Prazo abordagem" valor={formatDate(String(p.prazo_abordagem))} />
        )}
        {Boolean(p.observacao) && <Linha label="Observação" valor={String(p.observacao)} />}
        {ev.ator_nome && <Linha label="Por" valor={ev.ator_nome} />}
      </div>
    </Collapsible>
  );
}

// ─── Card de Dossiê ────────────────────────────────────────────────────────
function CardDossie({ eventos, oportunidade, dossieTexto }: {
  eventos: Evento[];
  oportunidade: Record<string, unknown>;
  dossieTexto?: string | null | undefined;
}) {
  const ev = eventos.find((e) => e.tipo === 'dossie_gerado');
  const texto = dossieTexto || (oportunidade.dossie_texto as string | null) || null;
  if (!ev && !texto) return null;
  const p = (ev?.payload as Record<string, unknown> | null) ?? {};
  const modelo = (oportunidade.dossie_modelo as string | null) ?? (p.modelo as string | null);
  const geradoEm = (oportunidade.dossie_gerado_em as string | null) ?? ev?.created_at;

  return (
    <Collapsible
      icon={FileText}
      title="Dossiê consultivo (IA)"
      subtitle={`${modelo ?? 'gerado'} · ${formatDate(geradoEm)}`}
    >
      <div className="flex flex-col gap-2 py-2">
        {Boolean(p.revisao_aplicada) && (
          <Linha label="Revisão aplicada" valor={String(p.revisao_aplicada)} />
        )}
        {Boolean(p.perfil_usado) && (
          <Linha label="Perfil usado" valor="sim — abordagem calibrada pelo perfil do cliente" />
        )}
        {texto && (
          <div className="mt-2 p-3 bg-surface-2 border border-border rounded-md">
            <DossieMarkdown texto={texto} />
          </div>
        )}
      </div>
    </Collapsible>
  );
}

// ─── Card de Abordagem (lista das tentativas registradas) ──────────────────
function CardAbordagem({ eventos }: { eventos: Evento[] }) {
  const tentativas = eventos.filter((e) => e.tipo === 'abordagem');
  if (tentativas.length === 0) return null;
  const ultima = tentativas[0];
  const p = (ultima?.payload as Record<string, unknown> | null) ?? {};
  const ultRes = ABORDAGEM_RESULTADOS.find((r) => r.v === p.resultado);

  return (
    <Collapsible
      icon={MessageSquare}
      title="Abordagem comercial"
      subtitle={`${tentativas.length} tentativa${tentativas.length > 1 ? 's' : ''}${ultRes ? ' · última: ' + ultRes.label : ''} · ${formatDate(ultima?.created_at)}`}
    >
      <ul className="flex flex-col gap-2 py-2">
        {tentativas.map((e) => {
          const pp = (e.payload as Record<string, unknown> | null) ?? {};
          const res = ABORDAGEM_RESULTADOS.find((r) => r.v === pp.resultado);
          const canal = ABORDAGEM_CANAIS.find((c) => c.v === pp.canal)?.label ?? (pp.canal ? String(pp.canal) : null);
          const acao = ABORDAGEM_ACOES.find((a) => a.v === pp.proxima_acao)?.label ?? null;
          return (
            <li key={e.id} className="border border-border rounded-md px-3 py-2 text-[0.82rem] flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-text">{res?.label ?? String(pp.resultado ?? '—')}</span>
                <span className="text-text-muted text-[0.72rem]">{formatDate(e.created_at)}</span>
              </div>
              {canal && (
                <span className="text-text-2 text-[0.78rem]">
                  {canal}{pp.quem_atendeu ? ` · com ${String(pp.quem_atendeu)}` : ''}
                </span>
              )}
              {Boolean(pp.anotacoes) && (
                <p className="text-text-2 text-[0.78rem]">{String(pp.anotacoes)}</p>
              )}
              {acao && (
                <span className="text-text-muted text-[0.72rem]">
                  → {acao}{pp.proximo_contato_em ? ` em ${formatDate(String(pp.proximo_contato_em))}` : ''}
                </span>
              )}
              {e.ator_nome && (
                <span className="text-text-muted text-[0.72rem]">por {e.ator_nome}</span>
              )}
            </li>
          );
        })}
      </ul>
    </Collapsible>
  );
}

// ─── Card de Proposta ──────────────────────────────────────────────────────
function CardProposta({ eventos, oportunidade }: { eventos: Evento[]; oportunidade: Record<string, unknown> }) {
  const ev = eventos.find((e) => e.tipo === 'proposta_enviada' || (e.tipo === 'movida' && e.estagio_para === 'proposta'));
  const valor = oportunidade.valor_fechado as number | null;
  if (!ev && !valor) return null;
  return (
    <Collapsible
      icon={Send}
      title="Proposta enviada"
      subtitle={ev ? formatDate(ev.created_at) : 'sem registro'}
    >
      <div className="flex flex-col gap-1 py-2">
        {valor != null && <Linha label="Valor" valor={formatBRL(valor)} />}
        {ev?.ator_nome && <Linha label="Por" valor={ev.ator_nome} />}
      </div>
    </Collapsible>
  );
}

// ─── Card de Negação (fechado_perdido OU recusado) ─────────────────────────
function CardNegado({ eventos, oportunidade }: { eventos: Evento[]; oportunidade: Record<string, unknown> }) {
  const estagio = String(oportunidade.estagio ?? '');
  const isRuido = estagio === 'recusado';

  // 'recusado' = ruído pós-triagem; 'fechado_perdido' = proposta negada.
  // Cada caminho registra motivo em colunas distintas.
  let motivoCat: string | null = null;
  let motivoTxt: string | null = null;
  let evento: Evento | undefined;

  if (isRuido) {
    motivoTxt = (oportunidade.motivo_ruido as string | null) ?? null;
    evento = eventos.find((e) => e.tipo === 'triada' &&
      (e.payload as Record<string, unknown> | null)?.status_sinal === 'ruido');
    if (!motivoTxt && evento) {
      motivoTxt = String((evento.payload as Record<string, unknown> | null)?.motivo ?? '') || null;
    }
  } else {
    motivoCat = (oportunidade.motivo_perda_cat as string | null) ?? null;
    motivoTxt = (oportunidade.motivo_perda as string | null) ?? null;
    evento = eventos.find((e) => e.tipo === 'fechada_perdido');
  }

  if (!motivoCat && !motivoTxt) return null;

  const catLabel = motivoCat
    ? (RECUSA_MOTIVOS.find((m) => m.v === motivoCat)?.label ?? motivoCat)
    : null;
  const subtitle = `${catLabel ?? (isRuido ? 'Ruído na triagem' : 'Sem categoria')} · ${formatDate(evento?.created_at) ?? '—'}`;

  return (
    <Collapsible icon={XCircle} title="Motivo da negação" subtitle={subtitle}>
      <div className="flex flex-col gap-1 py-2">
        {catLabel && <Linha label="Categoria" valor={catLabel} />}
        {motivoTxt && <Linha label="Detalhe" valor={motivoTxt} />}
        {evento?.ator_nome && <Linha label="Por" valor={evento.ator_nome} />}
      </div>
    </Collapsible>
  );
}

// ─── Componente principal — escolhe quais cards mostrar pelo estágio atual ──
export function FaseAnteriorCard({ estagio, eventos, oportunidade, dossieTexto }: Props) {
  // Mostra TODAS as fases anteriores ao estágio atual (mais recente primeiro)
  // assim o consultor vê a trilha completa do card sem trocar de tela.
  const cards: ReactNode[] = [];

  const ordem: Estagio[] = [
    'sinal_detectado', 'qualificacao', 'falso_positivo_servico',
    'dossie', 'abordagem', 'proposta',
  ];
  const idxAtual = ordem.indexOf(estagio);
  const isFechado = estagio === 'fechado_ganho' || estagio === 'fechado_perdido' || estagio === 'recusado';
  if (idxAtual <= 0 && !isFechado) return null;

  // Card de motivo da negação aparece primeiro pra fechado_perdido e recusado
  if (estagio === 'fechado_perdido' || estagio === 'recusado') {
    cards.push(<CardNegado key="negado" eventos={eventos} oportunidade={oportunidade} />);
  }

  // Triagem precede qualificacao e falso_positivo_servico
  if (estagio === 'qualificacao' || estagio === 'falso_positivo_servico' ||
      idxAtual >= ordem.indexOf('qualificacao') || isFechado) {
    cards.push(<CardTriagem key="triagem" eventos={eventos} />);
  }
  if (idxAtual >= ordem.indexOf('dossie') ||
      estagio === 'fechado_ganho' || estagio === 'fechado_perdido') {
    cards.push(<CardQualificacao key="qualif" eventos={eventos} oportunidade={oportunidade} />);
  }
  if (idxAtual >= ordem.indexOf('abordagem') ||
      estagio === 'fechado_ganho' || estagio === 'fechado_perdido') {
    cards.push(<CardDossie key="dossie" eventos={eventos} oportunidade={oportunidade} dossieTexto={dossieTexto} />);
  }
  if (idxAtual >= ordem.indexOf('proposta') ||
      estagio === 'fechado_ganho' || estagio === 'fechado_perdido') {
    cards.push(<CardAbordagem key="abord" eventos={eventos} />);
  }
  if (estagio === 'fechado_ganho' || estagio === 'fechado_perdido') {
    cards.push(<CardProposta key="prop" eventos={eventos} oportunidade={oportunidade} />);
  }

  const visiveis = cards.filter(Boolean);
  if (visiveis.length === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
        Fases anteriores
      </h3>
      <div className="flex flex-col gap-2">{visiveis}</div>
    </section>
  );
}
